import React, { useRef } from 'react';
import { resolveActiveSearcherId, visibleSearchers } from 'shared/searchers';
import { HeroTemplateProps } from '../../types';
import { resolveConfig, resolveBenefits, resolveSearcherTabs } from '../../resolveConfig';
import HeroSearchCard from '../../HeroSearchCard';
import HeroSearcherTabs from '../../HeroSearcherTabs';
import { HERO_TITLE_FALLBACK } from '../../../templateCodes';
import { manifest } from './manifest';
import '../../Hero.css';
import './HeroA.css';

const HeroA: React.FC<HeroTemplateProps> = ({
  config: partialConfig,
  onSearcherSelect,
  renderSearchForm,
  mobileSearchOpen = false,
  paisId,
}) => {
  const config = resolveConfig(manifest, partialConfig);
  const benefits = resolveBenefits(manifest, config);
  const searcherTabs = resolveSearcherTabs(manifest, config);
  const searchers = visibleSearchers(searcherTabs);
  const activeSearcherId = resolveActiveSearcherId(searcherTabs, config.activeSearcherTabId);
  const activeSearcher = searchers.find((searcher) => searcher.id === activeSearcherId);
  const showSearchers =
    config.showSearchers !== false && searchers.length > 0 && Boolean(activeSearcherId);
  const align = config.noSearcherTextAlign || 'left';
  const displayTitle = (config.title || '').trim() || HERO_TITLE_FALLBACK;
  const displayDescription = (config.description || '').trim();

  // Dedicated responsive image only (prototipo `has-responsive-bg`). Desktop
  // background stays as CSS cover — do not promote it to the mobile strip.
  const mobileImageUrl = (config.mobileBackgroundImageUrl || '').trim();
  const hasMobileBg = Boolean(mobileImageUrl);
  const mobileTabsRef = useRef<HTMLDivElement>(null);

  const rootClassName = [
    'hero',
    'hero--a',
    !config.showCopy && 'hero--no-copy',
    !showSearchers && 'hero--no-searchers',
    `hero--align-${align}`,
    hasMobileBg && 'hero--has-mobile-bg',
    config.fullBleed && 'hero--full-bleed',
    mobileSearchOpen && 'hero--mobile-search-open',
  ]
    .filter(Boolean)
    .join(' ');

  const rootStyle = {
    '--hero-title-color': config.titleColor,
    '--hero-description-color': config.descriptionColor,
    '--hero-brand': config.brandColor,
    '--hero-brand-2': config.brandColor2,
    '--hero-overlay-opacity': config.overlay / 100,
    '--hero-bg-image': config.backgroundImageUrl ? `url(${config.backgroundImageUrl})` : 'none',
  } as React.CSSProperties;

  const scrollMobileSearchersNext = () => {
    const nav = mobileTabsRef.current;
    if (!nav) return;
    const amount = Math.max(180, nav.clientWidth * 0.65);
    const atEnd = nav.scrollLeft + nav.clientWidth >= nav.scrollWidth - 8;
    nav.scrollTo({ left: atEnd ? 0 : nav.scrollLeft + amount, behavior: 'smooth' });
  };

  return (
    <section className={rootClassName} style={rootStyle}>
      <div className='hero__content'>
        {mobileImageUrl && (
          <div aria-hidden='true' className='hero__mobile-image-frame'>
            <img alt='' className='hero__mobile-image' src={mobileImageUrl} />
          </div>
        )}
        {config.showCopy && (
          <div className='hero__copy'>
            <h1>{displayTitle}</h1>
            {displayDescription ? <p>{displayDescription}</p> : null}
          </div>
        )}

        {showSearchers && (
          <div className='hero__search-module' id='hero-search-module'>
            <div className='hero__mobile-searcher-row'>
              <HeroSearcherTabs
                activeSearcherId={activeSearcherId}
                onSearcherSelect={onSearcherSelect}
                ref={mobileTabsRef}
                searchers={searchers}
              />
              {searchers.length > 1 ? (
                <button
                  aria-label='Ver más buscadores'
                  className='hero__mobile-searcher-next'
                  onClick={scrollMobileSearchersNext}
                  type='button'
                >
                  <svg fill='none' height='14' viewBox='0 0 8 14' width='8'>
                    <path
                      d='M1 1l6 6-6 6'
                      stroke='currentColor'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                    />
                  </svg>
                </button>
              ) : null}
            </div>
            <HeroSearchCard
              activeSearcherId={activeSearcherId}
              activeSearcherLabel={activeSearcher?.label ?? ''}
              benefits={benefits}
              paisId={paisId}
              renderSearchForm={renderSearchForm}
              showTitle={config.showSearchCardTitle !== false}
              title={config.searchCardTitle}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroA;
